import { useEffect, useState } from "react";
import { printingApi } from "@/pages/printing/api";
import type { PrintFormatFamily, PrintFormatRevision } from "@/pages/printing/types";
import { PrintPreview } from "@/pages/printing/PrintPreview";

interface Props {
  reportType: string;
  data: Record<string, any>;
  onClose: () => void;
}

export function PrintActionDialog({ reportType, data, onClose }: Props) {
  const [families, setFamilies] = useState<PrintFormatFamily[]>([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>("");
  const [activeRevision, setActiveRevision] = useState<PrintFormatRevision | null>(null);
  const [loading, setLoading] = useState(true);
  const [revLoading, setRevLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    printingApi.listFamilies()
      .then(async fams => {
        const list = Array.isArray(fams) ? fams : (fams as any)?.results ?? [];
        const cleanTarget = reportType.replace(/_/g, "").toLowerCase();
        const matching = list.filter((f: PrintFormatFamily) => f.report_type.replace(/_/g, "").toLowerCase() === cleanTarget);
        
        // For matching families, ensure we check if they have a published revision
        const availableFamilies: { family: PrintFormatFamily; publishedRevId: string; isDefault: boolean }[] = [];
        
        for (const fam of matching) {
          if (fam.latest_published_revision?.id) {
            availableFamilies.push({
              family: fam,
              publishedRevId: fam.latest_published_revision.id,
              isDefault: fam.latest_published_revision.is_default ?? false,
            });
          } else {
            // fetch revisions list for family to double check published revisions
            try {
              const revs = await printingApi.listRevisions(fam.id);
              const pub = revs.find(r => r.status.toLowerCase() === "published");
              if (pub) {
                availableFamilies.push({
                  family: fam,
                  publishedRevId: pub.id,
                  isDefault: pub.is_default ?? false,
                });
              }
            } catch (e) {
              console.error("Failed to list revisions for family", fam.id, e);
            }
          }
        }


        availableFamilies.sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));

        setFamilies(availableFamilies.map(item => ({
          ...item.family,
          latest_published_revision: item.family.latest_published_revision || ({ id: item.publishedRevId, is_default: item.isDefault } as any)
        })));

        if (availableFamilies.length > 0 && availableFamilies[0]?.family?.id) {
          setSelectedFamilyId(availableFamilies[0].family.id);
        }
      })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [reportType]);

  useEffect(() => {
    const selectedFamily = families.find(f => f.id === selectedFamilyId);
    const revSummary = selectedFamily?.latest_published_revision;
    if (!revSummary) {
      setActiveRevision(null);
      return;
    }

    setRevLoading(true);
    printingApi.getRevision(revSummary.id)
      .then(rev => setActiveRevision(rev))
      .catch(e => setError(String(e)))
      .finally(() => setRevLoading(false));
  }, [selectedFamilyId, families]);

  return (
    <div style={{
      position: "fixed",
      top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      direction: "rtl"
    }}>
      <div style={{
        background: "#fff",
        width: "90vw",
        height: "90vh",
        borderRadius: 8,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}>
        <header style={{ padding: "16px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: "1.2rem" }}>چاپ</h2>
          <button onClick={onClose} style={{ cursor: "pointer", background: "none", border: "none", fontSize: "1.2rem" }}>×</button>
        </header>

        <div style={{ padding: "16px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 16 }}>
          <label style={{ fontWeight: "bold" }}>انتخاب قالب چاپ:</label>
          {loading ? (
            <span>در حال بارگذاری...</span>
          ) : families.length === 0 ? (
            <span style={{ color: "#b91c1c" }}>هیچ قالب منتشر شده‌ای برای این نوع گزارش یافت نشد.</span>
          ) : (
            <select
              value={selectedFamilyId}
              onChange={e => setSelectedFamilyId(e.target.value)}
              style={{ padding: "8px", minWidth: 200, borderRadius: 4, border: "1px solid #d1d5db" }}
            >
              {families.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          )}
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: 16, display: "flex", justifyContent: "center", background: "#e5e7eb" }}>
          {error && <div style={{ color: "red" }}>{error}</div>}
          {revLoading && <div>در حال دریافت اطلاعات نسخه قالب...</div>}
          {!revLoading && activeRevision && (
            <PrintPreview
              elements={activeRevision.schema_json}
              pageSize={activeRevision.page_size}
              data={data}
            />
          )}
        </div>
      </div>
    </div>
  );
}
